// currentSteps ✅
// possibleMoves ✅
// evaluations ✅
// movementPreview (when we set target stepId)
// -> initialize ✅
// -> setCurrentStepId ✅
// -> setTargetStepId
// -> moveEvaluations
import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'

type Router = AppRouter['client']['moveEvaluations']
type InitialData = inferProcedureOutput<Router['initialize']>
type PossibleMoves = inferProcedureOutput<Router['getPossibleMoves']>
type MoveInstructions = inferProcedureInput<Router['moveEvaluations']>
type MovePreview = inferProcedureOutput<Router['getMovePreview']>

interface State {
	_cycleId?: string
	sourceSteps: InitialData
	sourceStepId?: string
	possibleMoves: PossibleMoves['possibleMoves']
	evaluations: PossibleMoves['evaluations']
	moveInstructions: MoveInstructions
	movePreview: MovePreview
	error?: string
	initialize: (cycleId: string) => Promise<void>
	setSourceStepId: (stepId?: string) => Promise<void>
	setMoveInstructions: (params?: Partial<Omit<MoveInstructions, 'cycleId'>>) => Promise<void>
	moveEvaluations: (notify: () => void) => Promise<void>
	clearError: () => void
}

const api = trpcClient.client.moveEvaluations
export const useMoveEvaluationsStore = create<State>()(
	devtools((set, get) => ({
		sourceStepId: undefined,
		sourceSteps: [],
		possibleMoves: [],
		evaluations: [],
		error: undefined,

		initialize: async (cycleId) => {
			try {
				const sourceSteps = await api.initialize.query({ cycleId })

				set({
					_cycleId: cycleId,
					moveInstructions: {
						cycleId,
						evaluationIds: [],
						targetStepId: '',
						discardSubmissions: false,
						applyToAll: false,
					},
					sourceSteps,
					error: undefined,
				})
			} catch (error) {
				set({
					error: error instanceof Error ? error.message : 'Error initializing move evaluations modal',
				})
			}
		},

		setSourceStepId: async (stepId) => {
			console.log(stepId)
			if (!stepId) {
				return set({
					sourceStepId: undefined,
					possibleMoves: [],
					evaluations: [],
				})
			}

			const { _cycleId } = get()

			if (!_cycleId) {
				return set({
					error: 'missing cycle id',
				})
			}

			try {
				const { possibleMoves, evaluations } = await api.getPossibleMoves.query({
					cycleId: _cycleId,
					stepId,
				})

				set({
					sourceStepId: stepId,
					possibleMoves,
					evaluations,
				})
			} catch (error) {
				set({
					error: error instanceof Error ? error.message : 'Error selecting source step id',
				})
			}
		},

		setMoveInstructions: async (params) => {
			if (!get().moveInstructions.cycleId) {
				return set({
					error: 'missing cycle id',
				})
			}

			set(({ moveInstructions }) => ({
				moveInstructions: {
					...moveInstructions,
					...params,
				},
			}))

			try {
				const { moveInstructions } = get()

				if (moveInstructions.targetStepId) {
					const movePreview = await api.getMovePreview.query(moveInstructions)

					set({
						movePreview,
					})
				}
			} catch (error) {
				set({
					error: error instanceof Error ? error.message : 'Error fetching move preview',
				})
			}
		},

		moveEvaluations: async (notify) => {
			const { moveInstructions } = get()

			try {
				await api.moveEvaluations.mutate(moveInstructions)

				set({
					moveInstructions: {
						cycleId: moveInstructions.cycleId,
						targetStepId: '',
						evaluationIds: [],
						discardSubmissions: false,
						applyToAll: false,
					},
					movePreview: undefined,
				})
				notify()
			} catch (error) {
				set({
					error: error instanceof Error ? error.message : 'Error moving evaluations',
				})
			}
		},

		clearError: () => {
			set({ error: undefined })
		},
	})),
)
